import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import RobotFace from "@/components/RobotFace";
import * as L from "lucide-react";

export function SentRepliesScreen({ user, go }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/sent-replies");
      setItems(data || []);
    } catch { toast.error("Couldn't load audit log"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load();   }, []);

  return (
    <div className="p-5 space-y-4" data-testid="sent-replies-screen">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-2xl font-display">Sent replies</div>
          <p className="text-sm text-[var(--rammy-text-sec)] mt-1">
            Every email Rammy has sent on your behalf. Nothing hidden.
          </p>
        </div>
        <button onClick={load} className="text-xs uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold" data-testid="sent-refresh">Refresh</button>
      </div>

      {loading && <div className="text-sm text-[var(--rammy-text-muted)] py-8 text-center">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-8 text-center" data-testid="sent-empty">
          <div className="mx-auto w-14 h-14 rounded-full bg-[var(--rammy-surface-alt)] flex items-center justify-center mb-3">
            <L.Send size={20} className="text-[var(--rammy-text-muted)]"/>
          </div>
          <div className="font-medium">No sends yet.</div>
          <p className="text-xs text-[var(--rammy-text-muted)] mt-1 max-w-xs mx-auto leading-relaxed">
            When you tap Send from Rammy, the full message appears here — searchable and never deletable by Rammy.
          </p>
        </div>
      )}

      <div className="space-y-2">
        {items.map(it => {
          const open = openId === it.id;
          return (
            <button
              key={it.id}
              onClick={()=>setOpenId(open ? null : it.id)}
              data-testid={`sent-item-${it.id}`}
              className="w-full text-left rounded-2xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] p-4"
            >
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                  <L.Check size={14} className="text-emerald-700"/>
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 justify-between">
                    <div className="text-sm font-medium truncate">{it.subject}</div>
                    <div className="text-[10px] font-mono text-[var(--rammy-text-muted)] shrink-0">
                      {new Date(it.sent_at).toLocaleDateString([], {month:"short", day:"numeric"})}
                    </div>
                  </div>
                  <div className="text-[11px] font-mono text-[var(--rammy-text-muted)] mt-0.5 truncate">to {it.to_email}</div>
                  <div className={`text-sm mt-2 leading-relaxed whitespace-pre-wrap ${open ? "" : "line-clamp-2"}`}>
                    {it.body}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
